import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Upload, Landmark, Plus, CheckCircle, X, Trash2, ChevronRight, Search,
  Calendar, ArrowRightLeft, Layers
} from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "../lib/api";
import CustomSelect from "../components/ui/CustomSelect";
import DatePicker from "../components/ui/DatePicker";
import { monthIST, fmtDateTime12 } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";
import PeriodAllocationModal from "../components/PeriodAllocationModal";
import {
  getPeriodAllocations,
  expandPeriodLines,
  loadAllocationsFromServer,
  formatMonthLabel,
  formatMonthShort
} from "../lib/periodAllocations";

const role = () => {
  try {
    return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
  } catch {
    return "owner";
  }
};

// One place that decides "does this statement line match the search box".
// Reconciliation is looked up by date and by amount at least as often as by
// narration, so the search covers narration / ref / ledger / rule / status /
// assigned vendor, both date formats a bank prints, and the debit-credit
// amount — with whitespace and case folded so "  ICICI " finds "icici".
const norm = (v) => String(v || "").toLowerCase().replace(/\s+/g, " ").trim();

const lineMatches = (l, q) => {
  const fields = [l.date, l.narration, l.ref_no, l.ledger_name, l.rule_applied,
                  l.status, l.assigned_vendor].map(norm);
  if (fields.some((f) => f.includes(q))) return true;
  if (/\d/.test(q)) {
    // 07-10-2026, 07/10/2026 and 7.10.2026 all reach the same field
    const alt = q.replace(/[/.]/g, "-");
    if (fields.some((f) => f.replace(/[/.]/g, "-").includes(alt))) return true;
    // DD-MM-YYYY typed by hand -> the ISO date the row actually stores
    const dm = q.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dm) {
      const iso = `${dm[3]}-${dm[2].padStart(2, "0")}-${dm[1].padStart(2, "0")}`;
      if (fields[0].includes(iso)) return true;
    }
  }
  // A pure amount query: 5000 must find the ₹5,000.00 debit
  if (/^[0-9][0-9.,]*$/.test(q)) {
    const amt = q.replace(/,/g, "");
    if (norm(l.debit).includes(amt) || norm(l.credit).includes(amt)) return true;
  }
  return false;
};

export default function Banking() {
  const isOwner = role() === "owner";
  const [accounts, setAccounts] = useState([]);
  const [bankId, setBankId] = useState("");
  const [tab, setTab] = useState("all");
  // Statement Lines filters: free text + ledger. Kept at component level so
  // they survive a status-tab switch and are cleared only by the user.
  const [lineSearch, setLineSearch] = useState("");
  const [ledgerFilter, setLedgerFilter] = useState(""); // "" = all · "__none" = not mapped
  const [allLines, setAllLines] = useState([]);
  const [balances, setBalances] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [catLedger, setCatLedger] = useState("");
  const _fom = new Date();
  _fom.setDate(1);
  const _iso = (d) => d.toISOString().slice(0, 10);
  const [recFrom, setRecFrom] = useState(_iso(_fom));
  const [recTo, setRecTo] = useState(_iso(new Date()));
  const [recon, setRecon] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [stmtData, setStmtData] = useState(null);
  const [view, setView] = useState("lines"); // lines | statements | pi
  // PI vs Bank: purchase invoices auto-matched to statement debits, so every
  // vendor bill shows its statement amount and what is still outstanding.
  const [piMatch, setPiMatch] = useState(null);
  const [piOpenVendor, setPiOpenVendor] = useState("");
  const [assignPick, setAssignPick] = useState({}); // statement line id -> vendor name
  const [assigningLine, setAssigningLine] = useState("");
  const [catVendor, setCatVendor] = useState("");
  const [rowVendorPick, setRowVendorPick] = useState({}); // line_id -> vendor
  // Who a debit may be pinned to (owner, 27 Oct 2026): "we need these 4
  // vendors only … in future [the ones] I create, not from these 9 vendors".
  // So the picker is NOT the Vendor Master register — it is exactly the
  // vendors PI matching knows: the ones carrying bills against this bank
  // account today (L'Oreal, SALON BASIC, SSK Enterprises, Stuff Solutions).
  // A vendor you add later shows up here as soon as it has a bill (or is
  // pinned to a line) — the register's unused 9 never leak in.
  const vendorOptions = useMemo(() => {
    const list = (piMatch?.vendors || []).map((v) => v.vendor).filter(Boolean);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [piMatch]);

  const selectedLedgerObj = useMemo(() => ledgers.find((l) => l.id === catLedger), [ledgers, catLedger]);
  const isPurchaseLedger = useMemo(() => {
    if (!selectedLedgerObj) return false;
    const grp = (selectedLedgerObj.grp || "").toLowerCase();
    const name = (selectedLedgerObj.name || "").toLowerCase();
    const code = String(selectedLedgerObj.code || "");
    return grp === "purchase" ||
      ["5010", "5020", "5030", "5040", "5050", "5060", "2010"].includes(code) ||
      /purchase|cogs|vendor|inventory|supplies|stock/i.test(name);
  }, [selectedLedgerObj]);

  const isLinePurchase = (l) => {
    if (!l) return false;
    const lid = l.ledger_id;
    const led = ledgers.find((x) => x.id === lid);
    if (led) {
      const grp = (led.grp || "").toLowerCase();
      const lName = (led.name || "").toLowerCase();
      const code = String(led.code || "");
      if (grp === "purchase" || ["5010", "5020", "5030", "5040", "5050", "5060", "2010"].includes(code) || /purchase|cogs|vendor|inventory|supplies|stock/i.test(lName)) {
        return true;
      }
    }
    const name = (l.ledger_name || "").toLowerCase();
    return /purchase|cogs|vendor|inventory|supplies|stock/i.test(name);
  };

  const [showCoa, setShowCoa] = useState(false);
  const [coaForm, setCoaForm] = useState({ name: "", code: "", grp: "Expense", keywords: "" });
  const [coaEdit, setCoaEdit] = useState({}); // id -> keywords
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAcctModal, setShowAcctModal] = useState(false);
  const [acctForm, setAcctForm] = useState({ name: "", bank_name: "", account_no: "", opening_balance: "" });
  const fileRef = useRef(null);

  // Period Allocation & Shift state
  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const [modalLine, setModalLine] = useState(null);
  const [allocationsVersion, setAllocationsVersion] = useState(0);

  useEffect(() => {
    loadAllocationsFromServer();
  }, []);

  useEffect(() => {
    const onLive = () => setAllocationsVersion((v) => v + 1);
    window.addEventListener("lss:data-changed", onLive);
    window.addEventListener("storage", onLive);
    return () => {
      window.removeEventListener("lss:data-changed", onLive);
      window.removeEventListener("storage", onLive);
    };
  }, []);

  const periodAllocations = useMemo(
    () => getPeriodAllocations(),
    [allocationsVersion]
  );

  // Period lines are reactively derived: expands split lines into their respective months
  // and shifts 100% lines to their target months.
  const periodLines = useMemo(() => {
    return expandPeriodLines(allLines, recFrom, recTo, periodAllocations);
  }, [allLines, recFrom, recTo, periodAllocations]);

  // `silent` = background poll. It must not blank the page to a spinner, and
  // it must not throw away the statement lines ticked for a batch
  // categorise/approve — only an explicit tab or date change resets those.
  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [a, l] = await Promise.all([
        api.get("/banking/accounts"),
        api.get("/accounts/chart"),
      ]);
      setAccounts(a.data?.accounts || []);
      setLedgers(l.data?.ledgers || []);
      if (!bankId && (a.data?.accounts || []).length > 0) setBankId(a.data.accounts[0].id);
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchLines = async (silent = false) => {
    if (!bankId) return;
    try {
      // "all" always pulls every line so the counts per status stay accurate.
      // The From/To date pickers narrow the whole transaction view to a period.
      const res = await api.get(`/banking/lines?bank_id=${bankId}&status=`);
      const all = res.data?.lines || [];
      setAllLines(all);
      if (!silent) setSelected(new Set());
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    }
  };

  // Opening / closing balance KPI (owner, 27 Oct 2026) — lives here, not on the
  // P&L. Same From → To period as the statement, straight from the uploaded
  // statement's own running-balance column; null when the file had none.
  const fetchBalances = async (silent = false) => {
    if (!bankId) return;
    try {
      const res = await api.get(
        `/banking/balances?bank_id=${bankId}${recFrom ? `&date_from=${recFrom}` : ""}${recTo ? `&date_to=${recTo}` : ""}`
      );
      setBalances(res.data || null);
    } catch (e) {
      if (!silent) console.error(e);
      else console.error(e);
    }
  };

  const fetchRecon = async () => {
    if (!bankId) return;
    try {
      const [res, st] = await Promise.all([
        api.get(`/banking/reconcile?bank_id=${bankId}`),
        api.get(`/banking/statements?bank_id=${bankId}`),
      ]);
      setRecon(res.data);
      setStmtData(st.data);
    } catch (e) {
      console.error(e);
    }
  };

  // PI vs Bank: purchase invoices auto-matched against statement debits — bill
  // amount from the PI, money-out from the statement, vendor a/c no from the
  // Vendor Matrix. Fetched only while its tab is open; a silent refresh must
  // not blank it, hence the flag.
  const fetchPiMatch = async (silent = false) => {
    if (!bankId) return;
    try {
      const res = await api.get(`/banking/pi-match?bank_id=${bankId}`);
      setPiMatch(res.data);
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    }
  };

  // Manual assignment (owner, 26 Oct 2026): pin a debit the heuristics could
  // not place onto a vendor, or clear the pin to fall back to auto-matching.
  // Both refetch the whole PI picture so every figure on the tab stays in step.
  const assignLine = async (lineId, vendor) => {
    const v = (vendor || "").trim();
    if (!v) {
      toast.error("Choose a vendor first");
      return;
    }
    setAssigningLine(lineId);
    try {
      await api.post("/banking/assign-line", { line_id: lineId, vendor: v });
      toast.success(`Mapped to ${v}`);
      await fetchPiMatch(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAssigningLine("");
    }
  };

  const clearAssignment = async (lineId) => {
    setAssigningLine(lineId);
    try {
      await api.post("/banking/assign-line", { line_id: lineId, vendor: "" });
      toast.success("Vendor unassigned, ledger cleared & uncategorized");
      await fetchPiMatch(true);
      fetchLines(true);
      fetchRecon();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAssigningLine("");
    }
  };

  const getRealSelectedIds = () =>
    Array.from(
      new Set(
        [...selected].map((id) => (id && typeof id === "string" && id.includes("__alloc__") ? id.split("__alloc__")[0] : id))
      )
    );

  const doUncategorize = async (ids = null) => {
    let targets = [];
    if (typeof ids === "string" && ids.trim()) {
      const real = ids.includes("__alloc__") ? ids.split("__alloc__")[0] : ids;
      targets = [real.trim()];
    } else if (Array.isArray(ids) && ids.length > 0) {
      targets = Array.from(new Set(ids.map((x) => typeof x === "string" && x.includes("__alloc__") ? x.split("__alloc__")[0] : x)));
    } else {
      targets = getRealSelectedIds();
    }
    if (targets.length === 0) {
      toast.error("Select lines to unassign and uncategorize");
      return;
    }
    try {
      const res = await api.post("/banking/uncategorize", { line_ids: targets });
      toast.success(`Unassigned ledger & uncategorized ${res.data?.uncategorized ?? 0} lines`);
      setSelected(new Set());
      setRowVendorPick({});
      fetchLines(); fetchRecon(); fetchAll(true);
      if (view === "pi") fetchPiMatch(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Always load PI matching — the vendor pickers on Statement Lines are fed
  // from it, so waiting for the "PI vs Bank" tab would leave them empty.
  useEffect(() => { fetchPiMatch(); }, [view, bankId]);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchLines(); fetchRecon(); fetchBalances(); }, [bankId, tab, recFrom, recTo]);

  // Bank feeds, statement uploads and PI activity arrive from anywhere — keep
  // every view of this screen live with no refresh button.
  // silent: nothing here may blank the table or drop a ticked selection.
  useAutoRefresh(() => {
    fetchAll(true);
    fetchLines(true);
    fetchBalances(true);
    fetchRecon();
    if (view === "pi") { fetchPiMatch(true); }
  }, 30000);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bankId) {
      if (!bankId) toast.error("Create/select a bank account first");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/banking/upload?bank_id=${bankId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      const d = res.data || {};
      const parts = [`Imported ${d.imported ?? 0} lines (${d.auto_categorized ?? 0} auto-categorized)`];
      if (d.duplicates_skipped) parts.push(`${d.duplicates_skipped} already present`);
      if (d.flagged) parts.push(`${d.flagged} row${d.flagged > 1 ? "s" : ""} needed manual review`);
      toast.success(parts.join(" · "));
      if (d.flagged_rows?.length) {
        toast.warning(d.flagged_rows.map((r) => `${r.date || "?"} — ${r.reason}`).join("\n"), { duration: 9000 });
      }
      fetchLines(); fetchRecon(); fetchAll();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleSel = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const doCategorize = async () => {
    if (selected.size === 0 || !catLedger) {
      toast.error("Select lines and a ledger");
      return;
    }
    try {
      const res = await api.post("/banking/categorize", {
        line_ids: getRealSelectedIds(),
        ledger_id: catLedger,
        line_vendors: isPurchaseLedger ? rowVendorPick : {},
      });
      toast.success(`Categorized ${res.data?.categorized ?? 0} lines`);
      setRowVendorPick({});
      fetchLines(); fetchRecon();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doApprove = async () => {
    if (selected.size === 0) {
      toast.error("Select lines to approve");
      return;
    }
    try {
      const targetIds = getRealSelectedIds();
      if (catLedger) {
        await api.post("/banking/categorize", {
          line_ids: targetIds,
          ledger_id: catLedger,
          line_vendors: isPurchaseLedger ? rowVendorPick : {},
        });
      }
      const res = await api.post("/banking/approve", { line_ids: targetIds });
      if (res.data?.skipped?.length > 0) {
        toast.warning(res.data.message || `Approved ${res.data?.approved ?? 0}, skipped ${res.data.skipped.length} without ledger`);
      } else {
        toast.success(`Approved ${res.data?.approved ?? 0} lines into books`);
      }
      setRowVendorPick({});
      fetchLines(); fetchRecon(); fetchAll(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doExclude = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/banking/exclude", { line_ids: getRealSelectedIds() });
      toast.success("Excluded — still visible in All Transactions");
      fetchLines(); fetchRecon();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleExcluded = async (line) => {
    const realId = line.parent_id || line.id;
    const restore = line.status === "excluded";
    try {
      await api.post("/banking/exclude", { line_ids: [realId], restore });
      toast.success(restore ? "Restored to the books" : "Excluded — still visible in All Transactions");
      fetchLines(); fetchRecon();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [clearing, setClearing] = useState(false);
  const [clearText, setClearText] = useState("");

  const doClearBankData = async () => {
    if (clearText !== "DELETE") return;
    setClearing(true);
    try {
      const res = await api.post("/banking/clear-data", { bank_id: bankId, confirm: true });
      const d = res.data || {};
      toast.success(`Bank data cleared — ${d.deleted_lines} transactions and ${d.deleted_vouchers} vouchers removed`);
      setShowClearModal(false);
      setClearText("");
      fetchLines(); fetchRecon(); fetchAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClearing(false);
    }
  };

  const saveAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/banking/accounts", { ...acctForm, opening_balance: Number(acctForm.opening_balance) || 0 });
      toast.success("Bank account added");
      setShowAcctModal(false);
      setAcctForm({ name: "", bank_name: "", account_no: "", opening_balance: "" });
      fetchAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  // Statement Lines filters. They narrow the ROWS, the select-all checkbox and
  // therefore what the Categorize / Approve / Exclude buttons act on — what you
  // see is what you tick. The status tabs and the period KPI cards above stay
  // period-wide on purpose, so their counts never contradict the date range.
  const ledgerFilterOptions = useMemo(() => {
    const seen = new Map();
    for (const l of periodLines) {
      if (l.ledger_id && !seen.has(l.ledger_id)) seen.set(l.ledger_id, l.ledger_name || l.ledger_id);
    }
    const opts = [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    opts.unshift({ value: "__none", label: "Not mapped yet" });
    // Keep a selection that the current period no longer produces selectable,
    // otherwise the dropdown would show a blank value with rows silently 0.
    if (ledgerFilter && !opts.some((o) => o.value === ledgerFilter)) {
      opts.push({ value: ledgerFilter, label: ledgers.find((l) => l.id === ledgerFilter)?.name || ledgerFilter });
    }
    return opts;
  }, [periodLines, ledgerFilter, ledgers]);

  // Search + ledger filter are applied to the WHOLE period first, so the table,
  // the status-tab counts and the summary tiles can never disagree about what
  // is on screen (owner, 27 Oct 2026: "the search box is not properly working").
  const searchedLines = useMemo(() => {
    const q = norm(lineSearch);
    return periodLines.filter((l) => {
      if (ledgerFilter === "__none" && l.ledger_id) return false;
      if (ledgerFilter && ledgerFilter !== "__none" && l.ledger_id !== ledgerFilter) return false;
      if (!q) return true;
      return lineMatches(l, q);
    });
  }, [periodLines, lineSearch, ledgerFilter]);

  const viewLines = useMemo(
    () => (tab === "all" ? searchedLines : searchedLines.filter((l) => l.status === tab)),
    [searchedLines, tab]
  );

  const lineFiltersOn = Boolean(lineSearch.trim() || ledgerFilter);

  // Selection totals count every ticked line of the period, even one a filter
  // has hidden — money you have already ticked must not vanish from the bar.
  const totals = useMemo(() => {
    let dr = 0, cr = 0;
    for (const l of periodLines) {
      if (selected.has(l.id)) {
        dr += l.debit || 0;
        cr += l.credit || 0;
      }
    }
    return { dr, cr };
  }, [periodLines, selected]);

  const saveCoaLedger = async (e) => {
    e.preventDefault();
    if (!coaForm.name.trim()) {
      toast.error("Ledger name required");
      return;
    }
    try {
      await api.post("/accounts/ledger", coaForm);
      toast.success("Ledger saved");
      setCoaForm({ name: "", code: "", grp: "Expense", keywords: "" });
      const l = await api.get("/accounts/chart");
      setLedgers(l.data?.ledgers || []);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveCoaKeywords = async (id) => {
    try {
      const led = ledgers.find((l) => l.id === id);
      await api.post("/accounts/ledger", { name: led.name, grp: led.grp, keywords: coaEdit[id] ?? led.keywords });
      toast.success("Keywords updated");
      const l = await api.get("/accounts/chart");
      setLedgers(l.data?.ledgers || []);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const activeBank = accounts.find((a) => a.id === bankId);

  // PI vs Bank status chips.
  const piBadge = (s) => ({
    settled: "bg-emerald-50 text-emerald-800 border-emerald-200",
    partial: "bg-amber-50 text-amber-800 border-amber-200",
    unpaid: "bg-rose-50 text-rose-800 border-rose-200",
    nil: "bg-slate-100 text-slate-500 border-slate-200",
  }[s] || "bg-slate-100 text-slate-600 border-slate-200");
  const piLabel = (s) => ({ settled: "Settled", partial: "Partial", unpaid: "Unpaid", nil: "No amount" }[s] || s);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-amber-700" />
            Bank Reconciliation
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Upload statements • Auto-categorize • Approve into books
            {activeBank && (activeBank.bank_name || activeBank.account_no) && (
              <span className="text-slate-700 font-bold"> — {activeBank.bank_name}{activeBank.bank_name && activeBank.account_no ? " • " : ""}{activeBank.account_no ? `A/c ${activeBank.account_no}` : ""}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CustomSelect
            options={(accounts || []).map((a) => ({ label: `${a.name}${a.bank_name ? ` — ${a.bank_name}` : ""}${a.account_no ? ` ••${a.account_no.slice(-4)}` : ""} (₹${(a.book_balance ?? 0).toLocaleString()})`, value: a.id }))}
            value={bankId}
            onChange={setBankId}
            placeholder="Select bank..."
            className="min-w-[260px]"
          />
          {isOwner && (
            <>
              <button onClick={() => setShowAcctModal(true)} title="Add a bank account" className="lss-btn-outline px-4 py-2 text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Bank
              </button>
              <button onClick={() => setShowCoa(true)} title="Add, edit or delete chart-of-accounts heads" className="lss-btn-outline px-4 py-2 text-sm flex items-center gap-2">
                Chart of Accounts
              </button>
              <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf,.xlsx,.xls,.csv" className="hidden" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading || !bankId} className="lss-btn-gold px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? "Importing..." : "Upload Statement"}
              </button>
            </>
          )}
          {isOwner && (
            <button
              onClick={() => { setClearText(""); setShowClearModal(true); }}
              disabled={!bankId}
              title="Delete bank statement data only"
              className="lss-btn-outline px-4 py-2 text-sm flex items-center gap-2 text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Delete Bank Data
            </button>
          )}
        </div>
      </div>

      {showClearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60" onClick={() => setShowClearModal(false)}>
          <div className="bg-white rounded-2xl border border-rose-200 w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-rose-50/70">
              <h3 className="font-extrabold text-rose-900">Delete bank data — {activeBank?.name}</h3>
              <button onClick={() => setShowClearModal(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                This permanently deletes, for this bank account only:
              </p>
              <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
                <li>{(stmtData?.total_transactions ?? 0)} statement transactions</li>
                <li>All vouchers these transactions posted to the books</li>
                <li>The statement upload history</li>
              </ul>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-800">Not affected</div>
                <div className="text-xs text-emerald-900 mt-0.5">
                  Bank account &amp; opening balance, chart of accounts, cash book, inventory,
                  attendance, payroll, MIS — all other modules stay untouched.
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  Type <span className="font-extrabold text-rose-700">DELETE</span> to confirm
                </label>
                <input
                  value={clearText}
                  onChange={(e) => setClearText(e.target.value)}
                  placeholder="DELETE"
                  className="lss-input w-full px-3 py-2 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowClearModal(false)} className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase">
                  Cancel
                </button>
                <button
                  onClick={doClearBankData}
                  disabled={clearText !== "DELETE" || clearing}
                  className="px-4 py-2 text-xs font-extrabold uppercase rounded-[10px] bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
                >
                  {clearing ? "Deleting..." : "Delete Bank Data"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {recon && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="lss-card p-4"><span className="lss-overline block">Statement Balance</span><span className="text-xl font-extrabold text-slate-950 tabular">{money(recon.statement_balance)}</span></div>
          <div className="lss-card p-4"><span className="lss-overline block">Book Balance</span><span className="text-xl font-extrabold text-emerald-800 tabular">{money(recon.book_balance)}</span></div>
          <div className="lss-card p-4"><span className="lss-overline block">Difference</span><span className={`text-xl font-extrabold tabular ${recon.difference === 0 ? "text-emerald-800" : "text-rose-700"}`}>{money(recon.difference)}</span></div>
          <div className="lss-card p-4"><span className="lss-overline block">Approved</span><span className="text-xl font-extrabold text-slate-950 tabular">{recon.approved_count}</span></div>
          <div className="lss-card p-4"><span className="lss-overline block">Pending</span><span className="text-xl font-extrabold text-amber-800 tabular">{recon.pending_count}</span></div>
        </div>
      )}

      <div className="lss-card p-3 sm:px-4 sm:py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 overflow-x-auto shrink-0">
          {[
            // Owner, 27 Oct 2026: the order the work happens in — see the
            // lines, then match them against the bills, then the uploads that
            // fed it.
            ["lines", "Statement Lines"],
            ["pi", "PI vs Bank"],
            ["statements", `Statements Loaded (${stmtData?.statements?.length || 0})`],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
                view === v ? "bg-slate-950 text-amber-300 shadow-md" : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2 shrink-0">
          <DatePicker label="From" value={recFrom} onChange={(e) => setRecFrom(e.target.value)} testId="bank-from" />
          <span className="text-slate-400 font-bold pb-2">→</span>
          <DatePicker label="To" value={recTo} onChange={(e) => setRecTo(e.target.value)} testId="bank-to" />
        </div>
      </div>
      {(!recFrom || !recTo || recFrom > recTo) && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl px-6 py-3 text-sm font-bold text-amber-900 text-center">
          Select the correct date range (From date must be on or before To date).
        </div>
      )}

      {view === "lines" && (
      <>
      <div className="lss-card p-4 space-y-3">
        {/* Row 1: Filter Tabs — flex-wrap so ALL tabs (All, Uncategorized, Categorized, Approved, Excluded) are ALWAYS 100% visible */}
        <div className="flex flex-wrap items-center gap-2">
          {["all", "uncategorized", "categorized", "approved", "excluded"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all ${
                tab === t ? "bg-slate-950 text-amber-300 shadow-md" : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              {t === "all"
                ? `All Transactions (${searchedLines.length})`
                : `${t} (${searchedLines.filter((l) => l.status === t).length})`}
            </button>
          ))}
        </div>

        {/* Row 1b: Search + ledger filter — narrows the visible rows, the
            select-all checkbox and the action counts together. */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={lineSearch}
              onChange={(e) => setLineSearch(e.target.value)}
              placeholder="Search narration, ref no, ledger…"
              aria-label="Search statement lines"
              data-testid="bank-line-search"
              className="lss-input lss-search py-2 text-xs w-full sm:w-64"
            />
            {lineSearch && (
              <button
                type="button"
                onClick={() => setLineSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="w-full sm:w-auto min-w-[200px]">
            <CustomSelect
              options={ledgerFilterOptions}
              value={ledgerFilter}
              onChange={setLedgerFilter}
              placeholder="All ledgers"
              searchable
              className="w-full sm:min-w-[220px]"
            />
          </div>
          {lineFiltersOn && (
            <button
              type="button"
              onClick={() => { setLineSearch(""); setLedgerFilter(""); }}
              className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 hover:text-rose-800 px-2"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-[11px] font-bold text-slate-500 tabular">
            Showing <b className="text-slate-900">{viewLines.length}</b> of {periodLines.length} line
            {periodLines.length === 1 ? "" : "s"}
            {lineFiltersOn && <> · <b className="text-slate-900">{searchedLines.length}</b> match the filters</>}
          </span>
        </div>

        {/* Row 2: Actions Bar — Dedicated row, perfectly visible and responsive */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[280px]">
            <div className="w-full sm:w-auto min-w-[220px]">
              <CustomSelect
                options={ledgers.map((l) => ({ label: `${l.name} (${l.grp})`, value: l.id }))}
                value={catLedger}
                onChange={setCatLedger}
                placeholder="Ledger to assign..."
                searchable
                className="w-full sm:min-w-[240px]"
              />
            </div>
            <button onClick={doCategorize} className="lss-btn-outline px-3.5 py-2 text-xs font-bold uppercase whitespace-nowrap">
              Categorize ({selected.size})
            </button>
            {isOwner && (
              <button onClick={doApprove} className="lss-btn-gold px-3.5 py-2 text-xs font-bold uppercase whitespace-nowrap shadow-xs">
                Approve ({selected.size})
              </button>
            )}
            <button onClick={doExclude} className="px-3.5 py-2 text-xs font-bold uppercase text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all whitespace-nowrap">
              Exclude
            </button>
            <button
              type="button"
              onClick={() => doUncategorize()}
              disabled={selected.size === 0}
              className="px-3.5 py-2 text-xs font-bold uppercase text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              title="Unassign ledger and revert status from approved/categorized to uncategorized"
            >
              Unassign Ledger ({selected.size})
            </button>
            <button
              type="button"
              onClick={() => {
                if (selected.size !== 1) return;
                const selId = [...selected][0];
                const targetLine = periodLines.find((l) => l.id === selId) || allLines.find((l) => l.id === selId);
                const parentId = targetLine?.parent_id || targetLine?.id;
                const realLine = allLines.find((l) => l.id === parentId) || targetLine;
                if (realLine) {
                  setModalLine(realLine);
                  setPeriodModalOpen(true);
                }
              }}
              disabled={selected.size !== 1}
              className={`px-3.5 py-2 text-xs font-bold uppercase rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 ${
                selected.size === 1
                  ? "bg-amber-600 hover:bg-amber-700 text-white border-amber-700 shadow-xs cursor-pointer"
                  : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60"
              }`}
              title={
                selected.size === 1
                  ? "Shift transaction to another month or allocate across multiple months"
                  : "Select exactly 1 row to shift or allocate period"
              }
            >
              <Calendar className="w-3.5 h-3.5" />
              Shift / Allocate Period {selected.size === 1 ? "(1)" : ""}
            </button>
          </div>
          {selected.size > 0 && (
            <div className="text-xs font-semibold text-amber-900 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 shrink-0">
              {selected.size} line{selected.size === 1 ? "" : "s"} selected
            </div>
          )}
        </div>
      </div>

      {tab === "all" && (
        <div className="text-xs font-semibold text-slate-600 bg-slate-100/70 border border-slate-200 rounded-lg px-4 py-2 flex flex-wrap items-center gap-x-2">
          <span>
            Showing <b className="text-slate-900">{periodLines.length}</b> of {allLines.length} uploaded
            transactions for <b className="text-slate-900">{recFrom || "start"} → {recTo || "today"}</b> —
            every line in this period is listed, including excluded ones. Change the From / To dates above to
            pick a different period.
          </span>
          {(recFrom || recTo) && allLines.length !== periodLines.length && (
            <button onClick={() => { setRecFrom(""); setRecTo(""); }}
              className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800 hover:underline">
              Show all dates
            </button>
          )}
        </div>
      )}

      {/* Opening / closing balance — moved here from the P&L (owner, 27 Oct
          2026): both numbers come from the uploaded statement's own balance
          column for exactly the From → To period above, never inferred. */}
      {balances && (
        <div className="lss-card px-5 py-3.5">
          {balances.opening_balance !== null && balances.opening_balance !== undefined ? (
            (() => {
              // UPI online payments arrive next day of credit day (terminal 0513300A0461793).
              // 1 Sep payment of ₹45,986.27 is from 31-Aug sales and is added to 1-Sep opening balance (owner instruction, 28-29 Oct 2026).
              // 30 Sep payment of ₹31,476.22 (credited 1-Oct) is from 30-Sep sales and is added to 30-Sep closing balance (owner instruction, 29 Oct 2026).
              const isSepStart = (!recFrom || recFrom <= "2026-09-01") && (!recTo || recTo >= "2026-09-01");
              const isSepEnd = (!recTo || recTo === "2026-09-30");
              const upiSep1Amt = 45986.27;
              const upiSep30Amt = 31476.22;

              const effectiveOpening = balances.upi_opening
                ? balances.opening_balance
                : (isSepStart && Number(balances.opening_balance) < 800000
                    ? Number((Number(balances.opening_balance) + upiSep1Amt).toFixed(2))
                    : Number(balances.opening_balance));

              const effectiveClosing = balances.upi_closing
                ? balances.closing_balance
                : (isSepEnd && Number(balances.closing_balance) < 550000
                    ? Number((Number(balances.closing_balance) + upiSep30Amt).toFixed(2))
                    : Number(balances.closing_balance));

              const netMovement = Number((effectiveClosing - effectiveOpening).toFixed(2));

              return (
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                  <div>
                    <span className="lss-overline block">Opening Balance</span>
                    <span className="text-base font-extrabold tabular text-slate-950">
                      {money(effectiveOpening)}
                    </span>
                    {isSepStart && (
                      <span className="block text-[10px] font-bold text-amber-800">
                        Incl. {money(upiSep1Amt)} 1-Sep UPI (31-Aug sales)
                      </span>
                    )}
                  </div>
                  <div className="text-slate-300 font-extrabold">→</div>
                  <div>
                    <span className="lss-overline block">Closing Balance</span>
                    <span className="text-base font-extrabold tabular text-slate-950">
                      {money(effectiveClosing)}
                    </span>
                    {isSepEnd && (
                      <span className="block text-[10px] font-bold text-amber-800">
                        Incl. {money(upiSep30Amt)} 1-Oct UPI (30-Sep sales)
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="lss-overline block">Net Movement</span>
                    <span
                      className={`text-base font-extrabold tabular ${
                        netMovement >= 0 ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {money(netMovement)}
                    </span>
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500">
                    From {balances.balance_source} · {recFrom || "start"} → {recTo || "today"} · {balances.balance_lines} of{" "}
                    {balances.month_lines} lines carry a balance
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 ml-auto">
                    Inflow {money(balances.month_credit)} · Outflow {money(balances.month_debit)}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
              Opening / closing balance unavailable — the statement loaded for this period has no running-balance
              column ({balances.month_lines} lines). Re-upload the statement PDF and both balances will be read
              straight from your bank&apos;s own figures.
            </div>
          )}
        </div>
      )}

      {tab === "all" && searchedLines.length > 0 && (
        <div className="lss-card px-5 py-3 grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">All Transactions</div>
            <div className="text-lg font-extrabold text-slate-950 tabular">{searchedLines.length}</div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">In the Books</div>
            <div className="text-lg font-extrabold text-emerald-700 tabular">
              {searchedLines.filter((l) => l.status !== "excluded").length}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Excluded</div>
            <div className="text-lg font-extrabold text-slate-500 tabular">
              {searchedLines.filter((l) => l.status === "excluded").length}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Total Debit</div>
            <div className="text-lg font-extrabold text-rose-700 tabular">
              {money(searchedLines.filter((l) => l.status !== "excluded").reduce((s, l) => s + (l.debit || 0), 0))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Total Credit</div>
            <div className="text-lg font-extrabold text-emerald-700 tabular">
              {money(searchedLines.filter((l) => l.status !== "excluded").reduce((s, l) => s + (l.credit || 0), 0))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Needs Action</div>
            <div className="text-lg font-extrabold text-amber-700 tabular">
              {searchedLines.filter((l) => l.status === "uncategorized" || l.status === "categorized").length}
            </div>
          </div>
        </div>
      )}

      <div className="lss-card overflow-hidden">
        <div className="overflow-auto max-h-[560px]">
          <table className="lss-table min-w-[900px]">
            <thead>
              <tr>
                <th><input type="checkbox" aria-label="Select all visible lines" checked={viewLines.length > 0 && viewLines.every((l) => selected.has(l.id))} onChange={() => setSelected(viewLines.length > 0 && viewLines.every((l) => selected.has(l.id)) ? new Set() : new Set(viewLines.map((l) => l.id)))} /></th>
                <th>Date</th>
                <th>Narration</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th>Ledger</th>
                <th>Rule</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {viewLines.map((l) => {
                const isRowSelected = selected.has(l.id);
                const isPurchase = isPurchaseLedger || isLinePurchase(l);
                const showVendorDropdown = isRowSelected && isPurchase;
                const isSplit = Boolean(l.is_allocated_split);
                const isShift = Boolean(l.is_allocated_shift);
                return (
                  <tr key={l.id} className={`hover:bg-slate-50/80 ${isSplit ? "border-l-4 border-l-indigo-500 bg-indigo-50/20" : isShift ? "border-l-4 border-l-purple-500 bg-purple-50/20" : l.status === "excluded" ? "bg-slate-100/70" : ""}`}>
                    <td><input type="checkbox" checked={isRowSelected} onChange={() => toggleSel(l.id)} /></td>
                    <td className="tabular font-semibold whitespace-nowrap">{l.date}</td>
                    <td className="text-xs font-medium max-w-[340px]">
                      <div className="truncate" title={l.narration}>
                        {l.status === "excluded" && (
                          <span className="text-[9px] font-extrabold uppercase text-slate-500 mr-1">(not in books)</span>
                        )}
                        {l.narration} {l.ref_no && <span className="text-slate-400">[{l.ref_no}]</span>}
                        {l.date === "2026-09-01" && (l.narration || "").includes("0513300A0461793") && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300">
                            T+1 Settlement · Added to 1-Sep Opening
                          </span>
                        )}
                        {l.date === "2026-10-01" && (l.narration || "").includes("0513300A0461793") && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300">
                            T+1 Settlement · Added to 30-Sep Closing
                          </span>
                        )}
                      </div>
                      {isSplit && (
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const parentLine = allLines.find((x) => x.id === (l.parent_id || l.id)) || l;
                              setModalLine(parentLine);
                              setPeriodModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold bg-indigo-100 hover:bg-indigo-200 text-indigo-950 border border-indigo-300 shadow-2xs transition-all cursor-pointer"
                            title="Click to view or edit period allocation"
                          >
                            <Layers className="w-3 h-3 text-indigo-700" />
                            <span>
                              Split ({l.split_index} of {l.split_total_parts}) · {money(l.split_amount)} of {money(l.total_original_amount)}
                              {l.split_month !== l.alloc_orig_month && ` (Allocated from ${formatMonthLabel(l.alloc_orig_month)})`}
                            </span>
                            {l.alloc_note && <span className="font-normal opacity-85">· {l.alloc_note}</span>}
                          </button>
                        </div>
                      )}
                      {isShift && (
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const parentLine = allLines.find((x) => x.id === (l.parent_id || l.id)) || l;
                              setModalLine(parentLine);
                              setPeriodModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold bg-purple-100 hover:bg-purple-200 text-purple-950 border border-purple-300 shadow-2xs transition-all cursor-pointer"
                            title="Click to view or edit period allocation"
                          >
                            <ArrowRightLeft className="w-3 h-3 text-purple-700" />
                            <span>
                              Shifted from {formatMonthLabel(l.alloc_orig_month)} ({money(l.debit || l.credit || 0)})
                            </span>
                            {l.alloc_note && <span className="font-normal opacity-85">· {l.alloc_note}</span>}
                          </button>
                        </div>
                      )}
                      {l.assigned_vendor && !showVendorDropdown && (
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold bg-amber-50 text-amber-900 border border-amber-200">
                            Vendor: {l.assigned_vendor}
                          </span>
                        </div>
                      )}
                      {showVendorDropdown && (
                        <div className="mt-1.5 flex items-center gap-1.5 p-1.5 bg-amber-50/90 border border-amber-300 rounded-lg" onClick={(e) => e.stopPropagation()}>
                          <span className="text-[10px] font-extrabold uppercase text-amber-900 whitespace-nowrap">Vendor:</span>
                          <select
                            value={rowVendorPick[l.id] !== undefined ? rowVendorPick[l.id] : (l.assigned_vendor || "")}
                            onChange={(e) => {
                              const val = e.target.value;
                              setRowVendorPick((prev) => ({ ...prev, [l.id]: val }));
                            }}
                            className="text-xs font-semibold text-slate-900 bg-white border border-amber-300 rounded px-2 py-1 outline-none min-w-[170px] focus:ring-1 focus:ring-amber-500"
                          >
                            <option value="">No vendor (unassigned)</option>
                            {vendorOptions.map((v) => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                          {l.assigned_vendor && (
                            <span className="text-[10px] text-slate-500 font-semibold">(current: {l.assigned_vendor})</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right tabular text-rose-700">{l.debit ? money(l.debit) : "—"}</td>
                    <td className="text-right tabular text-emerald-800">{l.credit ? money(l.credit) : "—"}</td>
                    <td className="text-xs font-bold text-slate-800">
                      {l.ledger_name || "—"}
                      {isRowSelected && catLedger && (
                        <div className="text-[10px] font-semibold text-amber-700 mt-0.5">
                          → {selectedLedgerObj?.name || ""}
                        </div>
                      )}
                    </td>
                    <td className="text-xs text-slate-500">{l.rule_applied || "—"}</td>
                    <td className="text-center"><span className={`lss-badge text-[10px] ${l.status === "approved" ? "bg-emerald-600 text-white border-emerald-700" : l.status === "categorized" ? "bg-sky-100 text-sky-950 border-sky-300" : l.status === "excluded" ? "bg-slate-200 text-slate-600 border-slate-300" : "bg-amber-100 text-amber-950 border-amber-300"}`}>{l.status}</span></td>
                    <td className="text-center">
                      {isOwner ? (
                        <div className="flex items-center justify-center gap-1.5">
                          {l.status === "excluded" ? (
                            <button onClick={() => toggleExcluded(l)}
                              className="text-[10px] font-extrabold uppercase tracking-wider text-sky-800 hover:text-sky-950 hover:underline">
                              Restore
                            </button>
                          ) : (
                            <button onClick={() => toggleExcluded(l)}
                              className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 hover:text-rose-700">
                              Exclude
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {viewLines.length === 0 && (
                <tr><td colSpan={9} className="text-center text-slate-500 font-medium py-8 text-xs">
                  {loading ? "Loading..." : lineFiltersOn ? "No matching lines." : "No lines. Upload a bank statement to begin."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
{selected.size > 0 && (
          <div className="px-6 py-3 border-t border-slate-200 bg-amber-50/60 text-xs font-bold text-slate-700">
            Selected {selected.size} lines — Dr {money(totals.dr)} · Cr {money(totals.cr)}
          </div>
        )}
      </div>
      </>
      )}

      {view === "statements" && stmtData && (
        <div className="lss-card px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <span className="text-xs font-extrabold uppercase tracking-wider text-slate-700">
              Statements Loaded ({stmtData.statements?.length || 0})
            </span>
            <span className="text-xs font-semibold text-slate-600">
              {stmtData.total_transactions} transactions · {money(stmtData.total_debit)} Dr ·{" "}
              {money(stmtData.total_credit)} Cr · {stmtData.pending_count} pending ·{" "}
              {stmtData.approved_count} approved
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>Statement File</th>
                  <th>Period</th>
                  <th className="text-right">Rows Found</th>
                  <th className="text-right">Added</th>
                  <th className="text-right">Overlap Skipped</th>
                  <th className="text-right">Needs Review</th>
                  <th className="text-right">Dr / Cr</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {(stmtData.statements || []).map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50/80">
                    <td className="text-xs font-bold text-slate-950">{s.filename || "(unnamed)"}</td>
                    <td className="text-xs tabular text-slate-600">
                      {s.date_from ? `${s.date_from} → ${s.date_to}` : "—"}
                    </td>
                    <td className="text-right tabular">{s.rows_parsed}</td>
                    <td className="text-right tabular font-bold text-emerald-700">{s.rows_imported}</td>
                    <td className="text-right tabular text-amber-700">{s.dup_skipped}</td>
                    <td className="text-right tabular text-rose-700">{s.flagged}</td>
                    <td className="text-right tabular text-xs text-slate-600">
                      {money(s.total_debit)} / {money(s.total_credit)}
                    </td>
                    <td className="text-xs tabular text-slate-500">{fmtDateTime12(s.created_at) || "—"}</td>
                  </tr>
                ))}
                {(stmtData.statements || []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-slate-500 text-xs py-6">
                      No statement uploaded yet. Use Upload Statement to load one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "pi" && (
      <>
        {(piMatch?.unmatched || []).length > 0 && (
          <div className="lss-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Unmatched debits — assign to a vendor ({piMatch.unmatched.length})
              </span>
              <span className="text-xs font-bold text-rose-700 tabular">
                {money(piMatch.summary?.unmatched_amount || 0)} not yet mapped to any bill
              </span>
            </div>
            <div className="px-6 py-3 border-b border-slate-200 bg-amber-50/70 text-[11px] font-semibold text-amber-900 leading-relaxed">
              These purchase/vendor money-out lines carry no matching a/c number or vendor name in the Vendor Matrix.
              Pick who was paid — the amount is applied to that vendor's bills, oldest first. (Non-purchase operating expenses like salaries, rent, and utilities are excluded).
            </div>
            <div className="overflow-auto max-h-[340px]">
              <table className="lss-table min-w-[820px]">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Narration</th>
                    <th className="text-right">Debit ₹</th>
                    <th>Assign to vendor</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {piMatch.unmatched.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50/70">
                      <td className="tabular font-bold whitespace-nowrap text-xs">{l.date}</td>
                      <td className="text-xs text-slate-700">
                        {(l.narration || "(no narration)").replace(/\s+/g, " ")}
                        {l.ref_no && <span className="text-slate-400 tabular"> · {l.ref_no}</span>}
                      </td>
                      <td className="text-right tabular font-extrabold text-rose-700 whitespace-nowrap">{money(l.debit)}</td>
                      <td>
                        <select
                          value={assignPick[l.id] || ""}
                          onChange={(e) => setAssignPick((p) => ({ ...p, [l.id]: e.target.value }))}
                          aria-label={`Vendor for ${(l.narration || l.ref_no || l.date).slice(0, 40)}`}
                          data-testid={`pi-assign-select-${l.id}`}
                          className="lss-input text-xs py-1.5 w-full min-w-[190px]"
                        >
                          <option value="">Select vendor…</option>
                          {vendorOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => assignLine(l.id, assignPick[l.id])}
                          disabled={assigningLine === l.id}
                          data-testid={`pi-assign-btn-${l.id}`}
                          className="lss-btn-gold px-3 py-1.5 text-[11px] disabled:opacity-50"
                        >
                          {assigningLine === l.id ? "Saving…" : "Assign"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="lss-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Vendor Bills — PI vs Statement (auto-matched)
            </span>
            <span className="text-xs text-slate-500 font-semibold flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Bills <b className="text-slate-900 tabular">{money(piMatch?.summary?.pi_total || 0)}</b></span>
              <span>·</span>
              <span>Matched from statement <b className="text-emerald-700 tabular">{money(piMatch?.summary?.matched || 0)}</b></span>
              <span>·</span>
              <span>Remaining <b className="text-rose-700 tabular">{money(piMatch?.summary?.remaining || 0)}</b></span>
              {(piMatch?.summary?.unmatched || 0) > 0 && (
                <>
                  <span>·</span>
                  <span>Unmatched debits <b className="text-rose-700 tabular">{piMatch.summary.unmatched} · {money(piMatch.summary.unmatched_amount || 0)}</b></span>
                </>
              )}
              {(piMatch?.summary?.assigned || 0) > 0 && (
                <>
                  <span>·</span>
                  <span>Assigned by hand <b className="text-amber-700 tabular">{piMatch.summary.assigned}</b></span>
                </>
              )}
            </span>
          </div>
          <div className="px-6 py-3 border-b border-slate-200 bg-amber-50/70 text-[11px] font-semibold text-amber-900 leading-relaxed">
            Bill amount from the purchase invoice · money out from the statement debit · vendor and bank a/c no from the Vendor Matrix.
            Matched across the whole statement history, so a bill paid in an earlier period is never shown as outstanding again.
            {" "}<b>Click a vendor row to see the statement lines it matched.</b>
          </div>
          <div className="overflow-auto max-h-[560px]">
            <table className="lss-table min-w-[880px]">
              <thead>
                <tr>
                  <th>Purchase Invoice</th>
                  <th>Vendor</th>
                  <th className="text-right">PI Amount ₹</th>
                  <th className="text-right">Statement Amount ₹</th>
                  <th className="text-right">Remaining ₹</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(piMatch?.vendors || []).map((v) => (
                  <React.Fragment key={v.vendor}>
                    <tr className="bg-slate-100 border-y border-slate-200">
                      <td colSpan={6} className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => setPiOpenVendor((o) => (o === v.vendor ? "" : v.vendor))}
                            data-testid={`pi-vendor-${v.vendor}`}
                            className="flex items-center gap-2 text-left"
                          >
                            <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform shrink-0 ${piOpenVendor === v.vendor ? "rotate-90" : ""}`} />
                            <span className="text-xs font-extrabold uppercase tracking-wide text-slate-950">{v.vendor}</span>
                            <span className="text-[11px] font-semibold text-slate-500">
                              {v.bank_account_no
                                ? `A/c ${v.bank_account_no}${v.bank_ifsc ? ` · IFSC ${v.bank_ifsc}` : ""}${v.bank_name ? ` · ${v.bank_name}` : ""}`
                                : "no bank a/c saved in Vendor Matrix"}
                            </span>
                          </button>
                          <span className="text-[11px] font-bold text-slate-600 tabular flex flex-wrap gap-x-4 gap-y-1">
                            <span>Statement debits {money(v.stmt_amount)} ({v.stmt_line_count})</span>
                            <span>Bills {money(v.pi_total)}</span>
                            <span className={v.remaining > 0 ? "text-rose-700" : "text-emerald-700"}>Remaining {money(v.remaining)}</span>
                          </span>
                        </div>
                      </td>
                    </tr>
                    {v.invoices.map((i) => (
                      <tr key={i.id} className="hover:bg-slate-50/70">
                        <td className="font-bold text-slate-900 whitespace-nowrap">
                          {i.invoice_number}
                          <span className="block text-[11px] text-slate-500 font-medium tabular">{i.invoice_date}</span>
                        </td>
                        <td className="text-slate-700 font-semibold">{v.vendor}</td>
                        <td className="text-right tabular font-bold">{money(i.total)}</td>
                        <td className="text-right tabular text-slate-700">{money(i.matched)}</td>
                        <td className={`text-right tabular font-extrabold ${i.remaining > 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(i.remaining)}</td>
                        <td>
                          <span className={`px-2 py-1 rounded-lg border text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap ${piBadge(i.status)}`}>
                            {piLabel(i.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {piOpenVendor === v.vendor && v.lines.map((l) => (
                      <tr key={l.id} className="bg-slate-50/80">
                        <td colSpan={6} className="px-4 py-2 text-[11px] text-slate-600 font-medium border-b border-slate-200/60">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <span className="tabular font-bold text-slate-700">{l.date}</span>
                              <span className="tabular font-bold text-slate-900"> · {money(l.debit)}</span>
                              <span className="text-slate-500"> · {(l.narration || "").replace(/\s+/g, " ")}</span>
                              <span className="text-amber-800 font-bold"> · assigned payment</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => clearAssignment(l.id)}
                              disabled={assigningLine === l.id}
                              className="px-2.5 py-1 rounded-md bg-rose-50 border border-rose-300 text-rose-700 hover:bg-rose-100 font-bold text-[10px] transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                              title="Unassign vendor, clear purchase ledger, and revert status to uncategorized"
                            >
                              {assigningLine === l.id ? "Unassigning…" : "Unassign Ledger & Vendor ✕"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {!piMatch && (
                  <tr><td colSpan={6} className="text-center text-slate-500 font-medium py-8 text-xs">Loading…</td></tr>
                )}
                {piMatch && (piMatch.vendors || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-500 font-medium py-8 text-xs">No purchase invoices to match yet.</td></tr>
                )}
              </tbody>
              {piMatch && (piMatch.summary?.invoices > 0) && (
                <tfoot>
                  <tr className="bg-amber-50/70 border-t border-amber-200/80">
                    <td className="px-3 py-2.5 text-xs font-extrabold uppercase tracking-wide text-amber-950">
                      Total — {piMatch.summary.invoices} bills · {piMatch.summary.vendors} vendors
                    </td>
                    <td />
                    <td className="text-right tabular font-black text-slate-950">{money(piMatch.summary.pi_total)}</td>
                    <td className="text-right tabular font-black text-slate-950">{money(piMatch.summary.matched)}</td>
                    <td className={`text-right tabular font-black ${piMatch.summary.remaining > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {money(piMatch.summary.remaining)}
                    </td>
                    <td className="text-[10px] font-bold uppercase text-amber-900 whitespace-nowrap">
                      {piMatch.summary.settled} settled · {piMatch.summary.partial} partial · {piMatch.summary.unpaid} unpaid
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </>
      )}

      {showAcctModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60" onClick={() => setShowAcctModal(false)}>
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-6 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-950">Add Bank Account</h3>
              <button onClick={() => setShowAcctModal(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveAccount} className="space-y-3">
              <input value={acctForm.name} onChange={(e) => setAcctForm({ ...acctForm, name: e.target.value })} placeholder="Account label (e.g. HDFC Current)" className="lss-input w-full text-sm" required />
              <div className="grid grid-cols-2 gap-3">
                <input value={acctForm.bank_name} onChange={(e) => setAcctForm({ ...acctForm, bank_name: e.target.value })} placeholder="Bank name" className="lss-input w-full text-sm" />
                <input value={acctForm.account_no} onChange={(e) => setAcctForm({ ...acctForm, account_no: e.target.value })} placeholder="A/c no (last 4)" className="lss-input w-full text-sm" />
              </div>
              <input type="number" step="0.01" value={acctForm.opening_balance} onChange={(e) => setAcctForm({ ...acctForm, opening_balance: e.target.value })} placeholder="Opening balance ₹" className="lss-input w-full text-sm" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowAcctModal(false)} className="lss-btn-outline px-4 py-2 text-xs">Cancel</button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs">Save ✓</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCoa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60" onClick={() => setShowCoa(false)}>
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <h3 className="font-extrabold text-slate-950">Chart of Accounts Manager</h3>
              <button onClick={() => setShowCoa(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <CoaManager
              ledgers={ledgers}
              coaForm={coaForm}
              setCoaForm={setCoaForm}
              saveCoaLedger={saveCoaLedger}
              coaEdit={coaEdit}
              setCoaEdit={setCoaEdit}
              saveCoaKeywords={saveCoaKeywords}
              onChanged={setLedgers}
            />
            <div className="px-6 py-3 border-t border-slate-200 text-xs text-slate-500 bg-slate-50/70">
              Keywords drive automatic classification on upload. Codes follow the 1000–6000 series.
            </div>
          </div>
        </div>
      )}

      <PeriodAllocationModal
        isOpen={periodModalOpen}
        onClose={() => {
          setPeriodModalOpen(false);
          setModalLine(null);
        }}
        line={modalLine}
        ledgers={ledgers}
        onSave={() => {
          setAllocationsVersion((v) => v + 1);
          setPeriodModalOpen(false);
          setModalLine(null);
        }}
      />
    </div>
  );
}

function CoaManager({ ledgers, coaForm, setCoaForm, saveCoaLedger, coaEdit, setCoaEdit, saveCoaKeywords, onChanged }) {
  const [grpFilter, setGrpFilter] = useState("All");
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const shown = (ledgers || []).filter((l) => {
    if (grpFilter !== "All" && l.grp !== grpFilter) return false;
    if (ql && !`${l.code || ""} ${l.name} ${l.keywords || ""}`.toLowerCase().includes(ql)) return false;
    return true;
  });
  const groups = ["All", ...Array.from(new Set((ledgers || []).map((l) => l.grp).filter(Boolean)))];
  return (
    <div className="p-6 overflow-y-auto space-y-4 flex-1">
      <form onSubmit={saveCoaLedger} className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <input value={coaForm.code} onChange={(e) => setCoaForm({ ...coaForm, code: e.target.value })} placeholder="Code" className="lss-input text-xs" />
        <input value={coaForm.name} onChange={(e) => setCoaForm({ ...coaForm, name: e.target.value })} placeholder="Ledger name" className="lss-input text-xs" />
        <select value={coaForm.grp} onChange={(e) => setCoaForm({ ...coaForm, grp: e.target.value })} className="lss-input text-xs">
          {["Income", "Expense", "Purchase", "Asset", "Liability", "Bank", "Cash"].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input value={coaForm.keywords} onChange={(e) => setCoaForm({ ...coaForm, keywords: e.target.value })} placeholder="keywords, comma separated" className="lss-input text-xs" />
        <button type="submit" className="lss-btn-gold px-3 py-1.5 text-xs">Add ✓</button>
      </form>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {groups.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGrpFilter(g)}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full border transition whitespace-nowrap ${
                grpFilter === g ? "bg-slate-950 text-amber-300 border-slate-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="relative ml-auto min-w-[200px] flex-1 max-w-[260px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code / name / keyword…"
            className="lss-input w-full pl-3 pr-3 py-1.5 text-xs"
          />
        </div>
        <span className="text-[11px] font-bold text-slate-500">{shown.length} of {(ledgers || []).length}</span>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <table className="lss-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Ledger</th>
              <th>Group</th>
              <th>Match Keywords</th>
              <th className="text-center">Delete</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} className="hover:bg-slate-50/80">
                <td className="tabular font-extrabold text-amber-800">{l.code || "—"}</td>
                <td className="font-bold text-xs">{l.name}</td>
                <td><span className="lss-badge text-[10px] bg-slate-100 text-slate-700">{l.grp}</span></td>
                <td>
                  <div className="flex gap-1.5">
                    <input
                      value={coaEdit[l.id] ?? l.keywords ?? ""}
                      onChange={(e) => setCoaEdit((prev) => ({ ...prev, [l.id]: e.target.value }))}
                      placeholder="keyword1, keyword2..."
                      className="lss-input flex-1 px-2 py-1 text-[11px]"
                    />
                    <button onClick={() => saveCoaKeywords(l.id)} className="text-emerald-700 hover:text-emerald-900 font-bold text-[11px] whitespace-nowrap">Save</button>
      </div>

                </td>
                <td className="text-center">
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ledger "${l.name}"? Only unused ledgers can be deleted.`)) return;
                      try {
                        await api.delete(`/accounts/ledger/${l.id}`);
                        toast.success("Ledger deleted");
                        const r = await api.get("/accounts/chart");
                        onChanged(r.data?.ledgers || []);
                      } catch (err) {
                        toast.error(errMsg(err));
                      }
                    }}
                    className="p-1.5 text-rose-600 hover:text-rose-900 hover:bg-rose-100 rounded-lg transition"
                    title="Delete ledger"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-500 font-medium py-8 text-xs">No ledgers match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
